"use client"

import { useState } from "react"
import { useLanguage } from "../../context/LanguageContext"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import "./ETrefCalculationModal.css"

const ETrefCalculationModal = ({ isOpen, onClose, onCalculate, farmId, projectId }) => {
  const { t } = useLanguage()
  const [selectedModels, setSelectedModels] = useState({
    general: ["general-penman-monteith"],
    alfalfa: [],
    grass: [],
  })
  const [hcref, setHcref] = useState(0.2)
  const [dateMode, setDateMode] = useState("single") // "single" or "range"
  const [singleDate, setSingleDate] = useState(new Date())
  const [dateRange, setDateRange] = useState([new Date(), new Date()])
  const [startDate, endDate] = dateRange
  const [isLoading] = useState(false)

  const generalModel = {
    id: "general-penman-monteith",
    name: "KSU Penman-Monteith",
    description: "General Penman-Monteith equation for reference evapotranspiration",
  }

  const alfalfaModels = [
    {
      id: "standardized-asce-penman-monteith",
      name: "ASCE Penman Monteith",
      description: "Standard method for alfalfa reference evapotranspiration",
    },
    {
      id: "original-penman",
      name: "Original Penman Monteith",
      description: "Classic evaporation model modified for alfalfa",
    },
    {
      id: "kimberly-1982-penman",
      name: "Kimberly 1982 Penman Monteith",
      description: "Refined Penman model developed at Kimberly, Idaho",
    },
    {
      id: "jensen-haise",
      name: "Jensen-Haise Method",
      description: "Temperature-based model for alfalfa reference ET",
    },
  ]

  const grassModels = [
    {
      id: "fao-56-penman-monteith",
      name: "FAO-56 Penman Monteith",
      description: "International standard for grass reference ET",
    },
    {
      id: "fao-24-penman",
      name: "FAO-24 Penman Monteith",
      description: "FAO adaptation of Penman equation",
    },
    {
      id: "turc-1961",
      name: "Turc Method",
      description: "Simplified model for humid climates",
    },
    {
      id: "fao-blaney-criddle",
      name: "FAO Blaney-Criddle Method",
      description: "Temperature-based model with daylight adjustment",
    },
    {
      id: "hargreaves-1985",
      name: "Hargreaves 1985 Method",
      description: "Temperature-based model with radiation estimation",
    },
    {
      id: "fao-24-pan-evaporation",
      name: "FAO-24 PAN Evaporation Calculation",
      description: "Based on measured pan evaporation with coefficients",
    },
  ]

  const handleModelChange = (category, modelId, checked) => {
    setSelectedModels((prev) => ({
      ...prev,
      [category]: checked
        ? [...(prev[category] || []), modelId]
        : (prev[category] || []).filter((id) => id !== modelId),
    }))
  }

  const handleGeneralModelChange = (checked) => {
    handleModelChange("general", generalModel.id, checked)
  }

  const handleCategoryChange = (category, checked) => {
    const models = category === "alfalfa" ? alfalfaModels : grassModels

    // Select/deselect all models in the category
    models.forEach((model) => {
      handleModelChange(category, model.id, checked)
    })
  }

  const handleHcrefChange = (value) => {
    setHcref(value)
  }

  // Check if all models in a category are selected
  const isAlfalfaCategorySelected = alfalfaModels.every((model) => selectedModels.alfalfa?.includes(model.id))
  const isGrassCategorySelected = grassModels.every((model) => selectedModels.grass?.includes(model.id))

  // Check if some models in a category are selected (for indeterminate state)
  const isAlfalfaCategoryIndeterminate = selectedModels.alfalfa?.length > 0 && !isAlfalfaCategorySelected
  const isGrassCategoryIndeterminate = selectedModels.grass?.length > 0 && !isGrassCategorySelected

  const handleCalculate = () => {
    if (
      selectedModels.general.length === 0 &&
      selectedModels.alfalfa.length === 0 &&
      selectedModels.grass.length === 0
    ) {
      alert(t("Please select at least one calculation model"))
      return
    }

    const calculationData = {
      models: selectedModels,
      hcref,
      dateMode,
      date: dateMode === "single" ? singleDate : null,
      dateRange:
        dateMode === "range"
          ? {
            startDate: startDate,
            endDate: endDate,
          }
          : null,
      farmId,
      projectId,
    }

    onCalculate(calculationData)
  }

  if (!isOpen) return null

  return (
    <div className="etref-modal-overlay">
      <div className="etref-modal">
        <div className="etref-modal-header">
          <h2>{t("Calculate WATref")}</h2>
          <button className="close-button" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="etref-modal-body">
          <div className="date-selection-section">
            <h3>{t("Select Date")}</h3>
            <div className="date-mode-selector">
              <label className={`date-mode-option ${dateMode === "single" ? "active" : ""}`}>
                <input
                  type="radio"
                  name="dateMode"
                  value="single"
                  checked={dateMode === "single"}
                  onChange={() => setDateMode("single")}
                />
                <span>{t("Single Date")}</span>
              </label>
              <label className={`date-mode-option ${dateMode === "range" ? "active" : ""}`}>
                <input
                  type="radio"
                  name="dateMode"
                  value="range"
                  checked={dateMode === "range"}
                  onChange={() => setDateMode("range")}
                />
                <span>{t("Date Range")}</span>
              </label>
            </div>

            <div className="date-picker-container">
              {dateMode === "single" ? (
                <DatePicker
                  selected={singleDate}
                  onChange={(date) => setSingleDate(date)}
                  className="date-picker"
                  dateFormat="yyyy-MM-dd"
                />
              ) : (
                <DatePicker
                  selectsRange={true}
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(update) => {
                    setDateRange(update)
                  }}
                  isClearable={false}
                  className="date-picker"
                  dateFormat="yyyy-MM-dd"
                />
              )}
            </div>
          </div>

          <div className="models-section">
            <h3>{t("Select Calculation Models")}</h3>

            <div className="model-selector-modal">
              {/* General Penman-Monteith Model */}
              <div className="general-model-section">
                <div className="general-model-item">
                  <label className="checkbox-container">
                    <input
                      type="checkbox"
                      checked={selectedModels.general?.includes(generalModel.id) || false}
                      onChange={(e) => handleGeneralModelChange(e.target.checked)}
                    />
                    <span className="model-name">
                      {generalModel.name}
                      <i className="fas fa-info-circle model-info-icon" title={generalModel.description}></i>
                    </span>
                  </label>
                  <div className="hcref-input-container">
                    <span>hcref [m]:</span>
                    <input
                      type="number"
                      value={hcref}
                      onChange={(e) => handleHcrefChange(Number.parseFloat(e.target.value) || 0.2)}
                      step="0.1"
                      min="0"
                      max="10"
                      className="hcref-input"
                      placeholder="0.2"
                      title="Reference crop height (hcref) in meters"
                    />
                  </div>

                </div>
              </div>

              <div className="model-categories">
                <div className="model-category">
                  <div className="category-header">
                    <input
                      type="checkbox"
                      id="modal-alfalfa-category"
                      className="category-checkbox"
                      checked={isAlfalfaCategorySelected}
                      ref={(checkbox) => {
                        if (checkbox) checkbox.indeterminate = isAlfalfaCategoryIndeterminate
                      }}
                      onChange={(e) => handleCategoryChange("alfalfa", e.target.checked)}
                    />
                    <label htmlFor="modal-alfalfa-category" className="category-title">
                      Tall Crop (Alfalfa)
                    </label>
                  </div>
                  <div className="model-list">
                    {alfalfaModels.map((model) => (
                      <div key={model.id} className="model-item">
                        <label className="checkbox-container">
                          <input
                            type="checkbox"
                            checked={selectedModels.alfalfa?.includes(model.id) || false}
                            onChange={(e) => handleModelChange("alfalfa", model.id, e.target.checked)}
                          />
                          <span className="model-name">
                            {model.name}
                            <i className="fas fa-info-circle model-info-icon" title={model.description}></i>
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="model-category">
                  <div className="category-header">
                    <input
                      type="checkbox"
                      id="modal-grass-category"
                      className="category-checkbox"
                      checked={isGrassCategorySelected}
                      ref={(checkbox) => {
                        if (checkbox) checkbox.indeterminate = isGrassCategoryIndeterminate
                      }}
                      onChange={(e) => handleCategoryChange("grass", e.target.checked)}
                    />
                    <label htmlFor="modal-grass-category" className="category-title">
                      Short Crop (Grass)
                    </label>
                  </div>
                  <div className="model-list">
                    {grassModels.map((model) => (
                      <div key={model.id} className="model-item">
                        <label className="checkbox-container">
                          <input
                            type="checkbox"
                            checked={selectedModels.grass?.includes(model.id) || false}
                            onChange={(e) => handleModelChange("grass", model.id, e.target.checked)}
                          />
                          <span className="model-name">
                            {model.name}
                            <i className="fas fa-info-circle model-info-icon" title={model.description}></i>
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="etref-modal-footer">
          <button className="cancel-button" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button className="calculate-button" onClick={handleCalculate} disabled={isLoading}>
            {isLoading ? (
              <>
                <div className="spinner"></div> {t("Calculating...")}
              </>
            ) : (
              t("Calculate")
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ETrefCalculationModal
